import apiClient from './api/axiosConfig';

/** Unwrap ApiResponse envelope { success, message, data } when present (RULE §9). */
const unwrap = (d) => (d && typeof d.success === 'boolean' && d.data !== undefined ? d.data : d);

class NotificationService {
  // Get notifications with pagination and filters
  async getNotifications(params = {}) {
    try {
      const response = await apiClient.get('/notifications/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  // Get notification by ID
  async getNotification(id) {
    try {
      const response = await apiClient.get(`/notifications/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification:', error);
      throw error;
    }
  }

  // Update notification
  async updateNotification(id, data) {
    try {
      const response = await apiClient.patch(`/notifications/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating notification:', error);
      throw error;
    }
  }

  // Delete notification
  async deleteNotification(id) {
    try {
      const response = await apiClient.delete(`/notifications/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  // Mark notification as read
  async markAsRead(id) {
    try {
      const response = await apiClient.patch(`/notifications/${id}/`, { status: 'read' });
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Mark notification as unread
  async markAsUnread(id) {
    try {
      const response = await apiClient.patch(`/notifications/${id}/`, { status: 'unread' });
      return response.data;
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      throw error;
    }
  }

  // Archive notification
  async archiveNotification(id) {
    try {
      const response = await apiClient.patch(`/notifications/${id}/`, { status: 'archived' });
      return response.data;
    } catch (error) {
      console.error('Error archiving notification:', error);
      throw error;
    }
  }

  // Get notification statistics
  async getStats() {
    try {
      const response = await apiClient.get('/notifications/stats/');
      return unwrap(response.data);
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }

  // Mark all notifications as read
  async markAllRead() {
    try {
      const response = await apiClient.post('/notifications/mark-all-read/');
      return unwrap(response.data);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // Bulk actions on notifications
  async bulkAction(action, notificationIds) {
    try {
      const response = await apiClient.post('/notifications/bulk-action/', {
        action,
        notification_ids: notificationIds
      });
      return unwrap(response.data);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  }

  // Get unread count
  async getUnreadCount() {
    try {
      const response = await apiClient.get('/notifications/unread-count/');
      return unwrap(response.data);
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  /**
   * SSE stream URL for real-time notification updates (EventSource).
   * Pass JWT so backend can authenticate; EventSource cannot send Authorization header.
   */
  getStreamUrl(token) {
    if (!token) return null;
    const base = apiClient.defaults?.baseURL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : '');
    return `${base.replace(/\/$/, '')}/notifications/stream/?token=${encodeURIComponent(token)}`;
  }

  // Get notification preferences
  async getPreferences() {
    try {
      const response = await apiClient.get('/notifications/preferences/');
      return response.data;
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  }

  // Update notification preferences
  async updatePreferences(data) {
    try {
      const response = await apiClient.patch('/notifications/preferences/', data);
      return response.data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  // Follow/Unfollow functionality
  async getFollows(params = {}) {
    try {
      const response = await apiClient.get('/notifications/follows/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching follows:', error);
      throw error;
    }
  }

  // Toggle follow status
  async toggleFollow(contentTypeId, objectId) {
    try {
      const response = await apiClient.post('/notifications/toggle-follow/', {
        content_type_id: contentTypeId,
        object_id: objectId
      });
      return unwrap(response.data);
    } catch (error) {
      console.error('Error toggling follow:', error);
      throw error;
    }
  }

  // Check if following
  async isFollowing(contentTypeId, objectId) {
    try {
      const response = await apiClient.get('/notifications/is-following/', {
        params: {
          content_type_id: contentTypeId,
          object_id: objectId
        }
      });
      return unwrap(response.data);
    } catch (error) {
      console.error('Error checking follow status:', error);
      throw error;
    }
  }

  // Update follow settings
  async updateFollowSettings(followId, data) {
    try {
      const response = await apiClient.patch(`/notifications/follows/${followId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating follow settings:', error);
      throw error;
    }
  }

  // Unfollow
  async unfollow(followId) {
    try {
      const response = await apiClient.delete(`/notifications/follows/${followId}/`);
      return response.data;
    } catch (error) {
      console.error('Error unfollowing:', error);
      throw error;
    }
  }

  // Send test notification
  async sendTestNotification(type = 'system', title = 'Test Notification', message = 'This is a test notification') {
    try {
      const response = await apiClient.post('/notifications/test/', {
        type,
        title,
        message
      });
      return unwrap(response.data);
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }

  // Helper method to get content type ID for an object
  async getContentTypeId(modelName) {
    try {
      // This would typically be a mapping or API call
      const contentTypeMap = {
        'post': 1,
        'business': 2,
        'event': 3,
        'ad': 4,
        'property': 5,
        'society': 6,
        'mosque': 7,
        'club': 8,
        'institution': 9,
        'announcement': 10
      };
      
      return contentTypeMap[modelName.toLowerCase()] || null;
    } catch (error) {
      console.error('Error getting content type ID:', error);
      return null;
    }
  }

  // Helper method to format notification time
  formatTimeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  }

  // Helper method to get notification type color
  getTypeColor(type) {
    const colors = {
      message: 'bg-blue-100 text-blue-800',
      ad_message: 'bg-green-100 text-green-800',
      follow: 'bg-purple-100 text-purple-800',
      like: 'bg-pink-100 text-pink-800',
      comment: 'bg-yellow-100 text-yellow-800',
      review: 'bg-orange-100 text-orange-800',
      announcement: 'bg-indigo-100 text-indigo-800',
      system: 'bg-gray-100 text-gray-800',
      reminder: 'bg-red-100 text-red-800',
      update: 'bg-cyan-100 text-cyan-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }

  // Helper method to get priority color
  getPriorityColor(priority) {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  }
}

export const notificationService = new NotificationService();
export default notificationService;
